import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { apiClient } from '../App';
import { toast } from 'sonner';
import { 
  Trophy, TrendingUp, TrendingDown, Calendar, Award, Flame, 
  X, ChevronRight, Droplets, Beef, Salad, Cookie, Dumbbell,
  Moon, Footprints, Apple, Heart, Pill
} from 'lucide-react';

// Map action icons to Lucide components
const ACTION_ICONS = {
  'droplets': Droplets,
  'beef': Beef,
  'salad': Salad,
  'cookie': Cookie,
  'dumbbell': Dumbbell,
  'moon': Moon,
  'footprints': Footprints,
  'apple': Apple,
  'heart': Heart,
  'pill': Pill,
  'circle-check': Award
};

const getMonthName = (month) => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  return months[month - 1];
};

const getPerformanceColor = (percentage) => {
  if (percentage >= 80) return 'text-green-400';
  if (percentage >= 50) return 'text-yellow-400';
  return 'text-red-400';
};

const getProgressColor = (percentage) => {
  if (percentage >= 80) return 'bg-green-500';
  if (percentage >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

const MonthlySummaryModal = ({ isOpen, onClose, summary, onMarkViewed }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [hasMarkedViewed, setHasMarkedViewed] = useState(false);

  // Reset hasMarkedViewed when summary changes
  useEffect(() => {
    if (summary) {
      setHasMarkedViewed(false);
    }
  }, [summary?.year, summary?.month]);

  if (!summary) return null;

  const markAsViewed = async () => {
    if (hasMarkedViewed || isClosing) return;
    setHasMarkedViewed(true);
    
    try {
      if (onMarkViewed) {
        await onMarkViewed(summary.year, summary.month);
      }
    } catch (err) {
      console.error('Failed to mark summary as viewed:', err);
    }
  };

  const handleClose = async () => {
    if (isClosing) return;
    setIsClosing(true);
    
    await markAsViewed();
    onClose();
    setIsClosing(false);
  };

  // Handle dialog open change - mark as viewed when closing
  const handleOpenChange = async (open) => {
    if (!open) {
      await handleClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-3">
            <Trophy className="text-[#EFB342]" size={28} />
            {getMonthName(summary.month)} {summary.year} Summary
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Overall Score */}
          <div className="bg-gradient-to-r from-[#EFB342]/20 to-[#EFB342]/5 rounded-2xl p-6 text-center">
            <h3 className="text-lg text-zinc-400 mb-2">Your Monthly Score</h3>
            <div className="text-5xl font-bold text-[#EFB342] mb-2">
              {summary.total_points} / {summary.max_possible_points}
            </div>
            <div className="text-xl text-zinc-300">
              {summary.overall_percentage}% Complete
            </div>
            <Progress 
              value={summary.overall_percentage} 
              className="h-3 mt-4 bg-zinc-800"
            />
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
              <Calendar className="mx-auto text-blue-400 mb-2" size={24} />
              <div className="text-2xl font-bold text-white">{summary.days_checked_in}</div>
              <div className="text-xs text-zinc-400">Days Checked In</div>
            </div>
            <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
              <Flame className="mx-auto text-orange-400 mb-2" size={24} />
              <div className="text-2xl font-bold text-white">{summary.longest_streak_this_month}</div>
              <div className="text-xs text-zinc-400">Best Streak</div>
            </div>
            <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
              <Award className="mx-auto text-[#EFB342] mb-2" size={24} />
              <div className="text-2xl font-bold text-white">{summary.badges_earned_this_month}</div>
              <div className="text-xs text-zinc-400">Badges Earned</div>
            </div>
          </div>

          {/* Action Breakdown */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="text-green-400" size={20} />
              Action Breakdown
            </h3>
            <div className="space-y-3">
              {summary.action_summaries?.map((action, index) => {
                const IconComponent = ACTION_ICONS[action.action_icon] || Award;
                return (
                  <div key={action.action_id} className="bg-zinc-800/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          action.percentage >= 80 ? 'bg-green-500/20' : 
                          action.percentage >= 50 ? 'bg-yellow-500/20' : 'bg-red-500/20'
                        }`}>
                          <IconComponent size={18} className={getPerformanceColor(action.percentage)} />
                        </div>
                        <span className="text-white font-medium">{action.action_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-bold ${getPerformanceColor(action.percentage)}`}>
                          {action.points_earned}/{action.points_possible}
                        </span>
                        <span className="text-zinc-500 text-sm">({action.percentage}%)</span>
                      </div>
                    </div>
                    <div className="w-full bg-zinc-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${getProgressColor(action.percentage)}`}
                        style={{ width: `${action.percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Strengths & Areas to Improve */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
              <h4 className="text-green-400 font-semibold mb-3 flex items-center gap-2">
                <TrendingUp size={18} />
                Top Strengths
              </h4>
              <ul className="space-y-2">
                {summary.top_strengths?.map((strength, i) => (
                  <li key={i} className="text-zinc-300 text-sm flex items-center gap-2">
                    <span className="text-green-400">#{i + 1}</span> {strength}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
              <h4 className="text-orange-400 font-semibold mb-3 flex items-center gap-2">
                <TrendingDown size={18} />
                Areas to Improve
              </h4>
              <ul className="space-y-2">
                {summary.areas_to_improve?.length > 0 ? (
                  summary.areas_to_improve.map((area, i) => (
                    <li key={i} className="text-zinc-300 text-sm flex items-center gap-2">
                      <span className="text-orange-400">•</span> {area}
                    </li>
                  ))
                ) : (
                  <li className="text-zinc-400 text-sm">Perfect score! No improvements needed! 🎉</li>
                )}
              </ul>
            </div>
          </div>

          {/* Motivational Message */}
          <div className="bg-[#EFB342]/10 border border-[#EFB342]/30 rounded-xl p-5">
            <p className="text-zinc-300 whitespace-pre-line leading-relaxed">
              {summary.motivational_message}
            </p>
          </div>

          {/* Close Button */}
          <Button
            onClick={handleClose}
            className="w-full bg-[#EFB342] text-black hover:bg-[#D99F30] font-semibold py-3"
            data-testid="close-summary-btn"
          >
            Great work 👊🏼  You're building habits that thrive!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MonthlySummaryModal;
